import {
  AutoComplete,
  Button,
  Card,
  InputNumber,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  notification,
} from "antd";
import { ReloadOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { useContext, useEffect, useState } from "react";

import { Context } from "../../../context";
import { Fetch, Post } from "../../../fetch";
import { useBank } from "./BankContext";

const { Column } = Table;
const { Text } = Typography;

const TOPICS_REASONING = [
  "general knowledge",
  "current affairs",
  "number series",
  "letter series",
  "analogies",
  "odd one out",
  "syllogisms",
  "mental arithmetic",
  "percentages & ratios",
  "direction sense",
  "blood relations",
  "coding-decoding",
  "clock & calendar",
];

const TOPICS_MATH = [
  "BODMAS & mixed operations",
  "fractions & decimals",
  "percentages",
  "percent change",
  "ratio & proportion",
  "profit, loss & discount",
  "simple interest",
  "averages",
  "LCM & HCF",
  "divisibility & remainders",
  "time, speed & distance",
  "trains & relative speed",
  "time & work",
  "pipes & cisterns",
  "linear equations",
];

const TOPICS = [
  "daily-use vocabulary",
  "SSC synonyms",
  "SSC antonyms",
  "banking exam vocabulary",
  "grammar — tenses",
  "grammar — prepositions",
  "grammar — articles",
  "grammar — subject-verb agreement",
  "idioms & phrases",
  "one-word substitution",
  "spelling correction",
  "office & workplace English",
  "travel & directions",
  "numbers, dates & time",
  "family & daily life",
];

/** Adaptive difficulty grades (setu quiz_gen._GRADE_GUIDE — keep in sync).
 * Grade wins over the legacy easy/medium/hard tag server-side. */
const GRADES = [
  { value: null, label: "auto (by level)" },
  { value: 1, label: "1 — A1 beginner" },
  { value: 2, label: "2 — A2 simple" },
  { value: 3, label: "3 — B1 aspirant" },
  { value: 4, label: "4 — B2 exam-grade" },
  { value: 5, label: "5 — C1 toughest" },
];

const STATUS_COLOR = { running: "processing", done: "success", failed: "error" };

/** Gemini drafts land as status "draft" — approve them in the Review tab
 * before they can ever reach the app. */
const GenerateTab = () => {
  const { token, apiUrl } = useContext(Context);
  const bankInfo = useBank();
  const TYPES = bankInfo.types;
  const topicSuggestions =
    { reasoning: TOPICS_REASONING, math: TOPICS_MATH }[bankInfo.bank] || TOPICS;

  const [level, setLevel] = useState(1);
  const [topic, setTopic] = useState("");
  const [categoryId, setCategoryId] = useState(null);
  const [levelId, setLevelId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [count, setCount] = useState(20);
  const [types, setTypes] = useState([]);
  const [grade, setGrade] = useState(null);
  const [running, setRunning] = useState(false);
  const [batches, setBatches] = useState([]);
  const [counter, setCounter] = useState(0);

  useEffect(() => {
    Fetch(`${apiUrl}/v4/quiz-config/batches?limit=30`, token, (result) => {
      if (result && result.batches) setBatches(result.batches);
    });
  }, [apiUrl, token, counter]);

  useEffect(() => {
    Fetch(`${apiUrl}/v4/quiz-config/?configKey=${bankInfo.configKey}`, token, (result) => {
      const rows = (result && result.config && result.config.categories) || [];
      setCategories(rows.filter((row) => row && row.enabled !== false && row.id));
    });
  }, [apiUrl, token, bankInfo.configKey]);

  // Poll while any batch is running.
  useEffect(() => {
    if (!batches.some((b) => b.status === "running")) return undefined;
    const id = setInterval(() => setCounter((n) => n + 1), 4000);
    return () => clearInterval(id);
  }, [batches]);

  const selectedCategory = categories.find((row) => row.id === categoryId) || null;
  const topicForGenerate = (selectedCategory && selectedCategory.topic) || topic.trim();

  const generate = () => {
    if (!topicForGenerate) {
      notification.warning({
        message: categoryId
          ? "This CMS category needs a topic on the Config tab"
          : "Topic is required",
      });
      return;
    }
    setRunning(true);
    Post(
      `${apiUrl}/v4/quiz-config/generate`,
      token,
      {
        level,
        topic: topicForGenerate,
        count,
        bank: bankInfo.bank,
        types: categoryId ? null : types.length ? types : null,
        difficulty_grade: grade ?? null,
        ...(categoryId ? { category_id: categoryId, level_id: levelId } : {}),
      },
      (result) => {
        setRunning(false);
        if (result && result.batchId) {
          notification.success({
            message: "Generation started",
            description: "Drafts stay out of the app until you approve them under Category questions.",
          });
          setCounter((n) => n + 1);
        } else {
          notification.error({
            message:
              (result && typeof result.detail === "string" && result.detail) ||
              "Generation failed to start",
          });
        }
      },
      () => {
        setRunning(false);
        notification.error({ message: "Generation failed to start" });
      }
    );
  };

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card title="Generate questions with Gemini" size="small">
        <Space size="large" wrap align="end">
          <Space direction="vertical" size={2}>
            <Text>Category</Text>
            <Select
              allowClear
              style={{ width: 240 }}
              placeholder="CMS category"
              value={categoryId}
              options={categories.map((row) => ({
                value: row.id,
                label: `${row.title} (${row.section === "earn" ? "Earn" : "Quiz"})`,
              }))}
              onChange={(value) => {
                setCategoryId(value || null);
                const row = categories.find((item) => item.id === value);
                if (!row) {
                  setLevelId(null);
                  return;
                }
                setTopic(row.topic || "");
                setTypes([]);
                const levels = (row.levels || []).filter((lv) => lv.enabled !== false);
                const first = levels[0];
                if (first) {
                  setLevelId(first.id);
                  setLevel(first.level || 1);
                } else {
                  setLevelId("1");
                  setLevel(1);
                }
              }}
            />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Category level</Text>
            <Select
              style={{ width: 180 }}
              placeholder="Level"
              value={levelId}
              disabled={!categoryId}
              options={((categories.find((row) => row.id === categoryId) || {}).levels || [])
                .filter((lv) => lv.enabled !== false)
                .map((lv) => ({ value: lv.id, label: lv.title || lv.id }))}
              onChange={(value) => {
                setLevelId(value);
                const row = categories.find((item) => item.id === categoryId);
                const lv = ((row && row.levels) || []).find((item) => item.id === value);
                if (lv && lv.level) setLevel(lv.level);
              }}
            />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Level</Text>
            <InputNumber min={1} value={level} onChange={(v) => setLevel(v || 1)} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>{categoryId ? "Topic (from CMS category)" : "Topic (pick or type your own)"}</Text>
            <AutoComplete
              style={{ width: 320 }}
              placeholder="e.g. daily-use vocabulary, SSC synonyms"
              value={categoryId ? (selectedCategory && selectedCategory.topic) || topic : topic}
              onChange={setTopic}
              disabled={!!categoryId}
              filterOption={(input, option) =>
                (option?.value || "").toLowerCase().includes(input.toLowerCase())
              }
              options={[
                ...new Set([
                  ...topicSuggestions,
                  ...batches.map((b) => b.topic).filter(Boolean),
                ]),
              ].map((t) => ({ value: t }))}
            />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Count</Text>
            <InputNumber min={1} max={50} value={count} onChange={(v) => setCount(v || 1)} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>
              {categoryId
                ? "Types (locked to category topic)"
                : bankInfo.bank === "reasoning"
                  ? "Types (empty = this topic only)"
                  : "Types (empty = mix of all)"}
            </Text>
            <Select
              mode="multiple"
              allowClear
              style={{ width: 320 }}
              value={categoryId ? [] : types}
              onChange={setTypes}
              disabled={!!categoryId}
              options={TYPES.map((t) => ({ value: t, label: t }))}
            />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Difficulty grade</Text>
            <Select
              style={{ width: 190 }}
              value={grade}
              onChange={setGrade}
              options={GRADES}
            />
          </Space>
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            loading={running}
            onClick={generate}
          >
            Generate drafts
          </Button>
        </Space>
        <div style={{ marginTop: 8 }}>
          <Text type="secondary">
            Drafts go to the Review Queue — nothing is served to users until
            approved there.
            {bankInfo.bank === "reasoning"
              ? " Pick a CMS category to generate for that category topic only (General knowledge → GK, not series/coding). Types stay empty so leftover chips cannot mix families."
              : ""}
          </Text>
        </div>
      </Card>

      <Card
        title="Generation batches"
        size="small"
        extra={
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={() => setCounter((n) => n + 1)}
          >
            Refresh
          </Button>
        }
      >
        <Table
          dataSource={batches
            .filter((b) => (b.bank || "english") === bankInfo.bank)
            .map((b) => ({ ...b, key: b._id }))}
          pagination={false}
          size="small"
        >
          <Column title="Started" dataIndex="created_at" width={170} />
          <Column title="Level" dataIndex="level" width={70} />
          <Column title="Topic" dataIndex="topic" />
          <Column
            title="Types"
            render={(_, r) => (r.types || []).join(", ") || "all"}
          />
          <Column
            title="Grade"
            width={100}
            render={(_, r) =>
              r.difficulty_grade != null
                ? `G${r.difficulty_grade}`
                : r.difficulty || "auto"
            }
          />
          <Column title="Requested" dataIndex="count_requested" width={90} />
          <Column title="Inserted" dataIndex="count_generated" width={90} />
          <Column
            title="Status"
            width={110}
            render={(_, r) => (
              <Tag color={STATUS_COLOR[r.status] || "default"}>{r.status}</Tag>
            )}
          />
          <Column
            title="Error"
            render={(_, r) =>
              r.error ? <Text type="danger">{r.error}</Text> : null
            }
          />
        </Table>
      </Card>
    </Space>
  );
};

export default GenerateTab;
